/**
 * 残高の画面（/money/prepaid）の招待クレジットの欄（2026-09-29 creator/launch-money・Creator の部品）。
 *   - まだ使っていない → 「招待コードを入れる」の欄 → POST /v1/developers/me/invite-code { code }
 *   - メールの確認待ち → 「メールの確認が済むと入ります」
 *   - 入った → 欄は出さない（「うち招待クレジット 残り {remaining} / {granted}」は PrepaidPage が残高の下に出す・2026-09-29 launch-close-2）
 * 状態は GET /v1/money/prepaid の invite（Money）から読む（inviteCode.ts の readInviteStanding / inviteView）。
 *
 * 置き場所: pages/money/MoneyShell.tsx が前払い残高の画面の下に出す。WHY PrepaidPage の中に書かないか: 前払いの画面は Money の持ち物で、
 *   指示書は「Money の画面の中なら Money に頼む」。入口の枠（MoneyShell）だけを Creator が触り、残高と同じ画面に出す。
 *   Money が前払いの画面の中（残高のタイルの近く）へ動かすなら、この部品をそのまま置けばよい（onRedeemed で残高を読み直させる）。
 *
 * 見つけやすさ（2026-10-29 core/open-signup-build・founder「招待コードはクレジット獲得のみ」= 招待なしで登録した人は $0 から）:
 *   id="invite-code" を付け、ホームの入金の段・お金以外の画面の帯 'start'・登録の「使えませんでした」の 1 枚から /money/prepaid#invite-code で直に来られる
 * 入れられるのは開発者のセッション（kds_）だけ（サーバーが kdt_ = MCP の委任を 403 で断る）。ダッシュボードはいつもセッションなので気にしない。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { api, type ApiError } from '../api';
import { useResource } from '../hooks';
import { errorText, useT } from '../i18n';
import { inviteOutcome, inviteView, readInviteInput, readInviteStanding } from '../inviteCode';
import { money } from '../money';

export function InviteCodeCard({ onRedeemed }: { onRedeemed?: () => void }) {
  const { t } = useT();
  const r = useResource<{ invite?: unknown; currency?: string }>('/v1/money/prepaid');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const location = useLocation();
  // 「招待コードがあれば入れる」のリンク（ホームの入金の段・帯・登録の「使えませんでした」の 1 枚 = /money/prepaid#invite-code・2026-10-29 core/open-signup-build）
  //   から来たら、この欄まで動かす（react-router は # へ動かさない・ProjectNameCard と同じ）。読み込みの後に出るので data も見る
  const loaded = !!r.data;
  //   2026-10-29 core/open-signup-polish: 1 回動かすだけだと、上の前払いの画面（履歴・料金のしくみなど・別の GET）が後から読み込まれて
  //   欄が下へ押し出され、撮った写真（screens/billing-invite-code-*）では欄が画面の下の端で切れていた。→ 来てから 2 秒だけ、ページの高さが
  //   変わるたびに欄へ動かし直す。人が自分でスクロール・キー・タッチをしたらすぐやめる（読んでいる所を奪わない）
  useEffect(() => {
    if (!loaded || location.hash !== '#invite-code') return;
    const go = () => document.getElementById('invite-code')?.scrollIntoView({ block: 'start' });
    go();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(go);
    ro.observe(document.body);
    const stop = () => {
      ro.disconnect();
      window.clearTimeout(timer);
      for (const ev of ['wheel', 'touchstart', 'keydown'] as const) window.removeEventListener(ev, stop);
    };
    const timer = window.setTimeout(stop, 2000);
    for (const ev of ['wheel', 'touchstart', 'keydown'] as const) window.addEventListener(ev, stop, { passive: true });
    return stop;
  }, [loaded, location.hash]);
  // 読めない（古いサーバー・一時的な失敗）ときは欄ごと出さない: 前払いの画面そのもののエラーは PrepaidPage が出す（同じ GET）
  if (!r.data) return null;
  const currency = r.data.currency ?? 'usd';
  const view = inviteView(readInviteStanding(r.data.invite));
  // 入った後（credited）は欄ごと出さない（2026-09-29 launch-close-2・Money の前払いの道 18）: 「うち招待クレジット 残り {x} / {granted}」の行は
  //   前払いの画面（PrepaidPage）が残高のすぐ下に出す = ここでも出すと 2 回同じことを言う。入れた直後の「入りました」の知らせだけは見せる
  if (view.kind === 'credited' && !msg) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    const input = readInviteInput(code);
    // 空・形の違いは送らない（サーバーも同じ 400 invalid_invite_code を返す = 同じ文言）
    if (input.kind !== 'ok') {
      setMsg({ ok: false, text: t('error.invalid_invite_code') });
      return;
    }
    setBusy(true);
    try {
      const res = await api<unknown>('/v1/developers/me/invite-code', { method: 'POST', body: { code: input.code } });
      const out = inviteOutcome(res);
      const amount = money(out.kind === 'credited' ? out.credited : 0, out.kind === 'credited' ? out.currency : currency);
      setMsg({ ok: true, text: out.kind === 'credited' && out.pending ? t('invite.card.donePending', { amount }) : t('invite.card.done', { amount }) });
      setCode('');
      r.reload();
      onRedeemed?.();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err as ApiError) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" id="invite-code" aria-labelledby="invite-card-title">
      <h2 className="card-title" id="invite-card-title">
        {t('invite.card.title')}
      </h2>
      <p className="muted small">{t('invite.card.lead')}</p>
      {view.kind === 'pending' && (
        <p className="alert alert-info" role="status">
          {t('invite.card.pending', { amount: money(view.granted, currency) })}
        </p>
      )}
      {msg && (
        <p className={`alert ${msg.ok ? 'alert-ok' : 'alert-error'}`} role={msg.ok ? 'status' : 'alert'}>
          {msg.text}
        </p>
      )}
      {view.kind === 'form' && (
        <form className="form-inline" onSubmit={(e) => void submit(e)}>
          <label className="field" htmlFor="invite-redeem">
            <span>{t('invite.card.label')}</span>
            {/* autocomplete=off・name なし: 1 回限りのコード（パスワード管理・入力の履歴に残さない） */}
            <input id="invite-redeem" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={64} value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy || !code.trim()}>
            {busy ? t('common.working') : t('invite.card.submit')}
          </button>
        </form>
      )}
    </section>
  );
}
